import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSpinner,
  Icon,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function FlagCommandList<T extends boolean = false>({
  selected = [],
  ...props
}: Props<T, string>) {
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Flags)

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return data
    const normalizedQuery = query.toLowerCase()
    return data?.filter((flag) => flag.name.toLowerCase().includes(normalizedQuery))
  }, [data, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      props.onSelect(newSelection)
    },
    [props.onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (props.multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        props.onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        props.onSelect([id])
      }
    },
    [props.multiselect, selected, props.onSelect],
  )

  const handleClear = useCallback(() => {
    props.onSelect([])
  }, [props.onSelect])

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      <CommandInput
        placeholder="Search flags"
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="py-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={props.multiselect}
          />
          {props.allowEmpty && (
            <CommandItem onSelect={handleClear} className="gap-2">
              <Icon name="x" />
              {props.allowEmptyLabel || 'Clear'}
            </CommandItem>
          )}
          {visibleItems?.map((flag) => (
            <CommandCheckItem
              key={flag.id}
              value={flag.id}
              keywords={[flag.name]}
              className="group mx-1 flex gap-3"
              selected={selected.includes(flag.id)}
              onSelect={() => handleToggleItem(flag.id)}
              multiselect={props.multiselect}
            >
              <Icon
                name="flag"
                weight="fill"
                style={{ fill: flag.meta.color, stroke: flag.meta.color }}
              />
              <span className="truncate">{flag.name}</span>
            </CommandCheckItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

interface Props<T extends boolean | undefined, V extends string> extends Omit<
  BaseCommandListProps<T, V>,
  'placeholder'
> {}
